import type { InsertIssueAssessmentMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  InsertIssueAssessmentDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import { coerceToArray, type InsertIssueAssessmentInput } from './types';

export const useInsertIssueAssessment = createMutationHook({
  name: 'insertIssueAssessment',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.issueAssessment.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: (
    variables: InsertIssueAssessmentInput
  ): InsertIssueAssessmentInput => {
    const { ThirdPartyResponsibleIds, ...scalarVariables } = variables;

    return {
      ...scalarVariables,
      TagTypeIds: coerceToArray(variables.TagTypeIds),
      DepartmentTypeIds: coerceToArray(variables.DepartmentTypeIds),
      RegulationsBreachedIds: coerceToArray(variables.RegulationsBreachedIds),
      AssociatedControlIds: coerceToArray(variables.AssociatedControlIds),
      PoliciesBreachedIds: coerceToArray(variables.PoliciesBreachedIds),
      ...(ThirdPartyResponsibleIds != null
        ? {
            ThirdPartyResponsibleIds: coerceToArray(ThirdPartyResponsibleIds),
          }
        : {}),
    };
  },
  invalidate: (trpc) => [
    // COI-889: router-level keys — COI-471 parity for the Apollo twin's `issue`
    // and `issue_assessment` evictions (the register embeds the assessment).
    trpc.frontend.issue.pathKey(),
    trpc.frontend.issueAssessment.pathKey(),
    // COI-471 parity: Apollo twin also evicts `control` and `tag_type`.
    trpc.frontend.control.pathKey(),
    trpc.frontend.tag.pathKey(),
    // COI-889: the ribbon's issue tile excludes issues whose assessment is
    // Closed (`getRibbonIssues` base condition: no assessment OR
    // `assessment.Status != closed`), so writing the assessment Status moves
    // the count.
    trpc.frontend.myItems.pathKey(),
  ],
  mapResponse: (result: { Id: string }): InsertIssueAssessmentMutation => ({
    insertChildIssueAssessment: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertIssueAssessmentDocument,
  graphqlVariables: (variables: InsertIssueAssessmentInput) => ({
    ...variables,
  }),
  evictFields: [
    'issue',
    'tag_type',
    'issue_assessment',
    'issue_parent',
    'control',
    'issue_assessment_aggregate',
    'issue_assessment_audit',
  ],
  refetchQueries: [namedOperations.Query.getIssueById],
});
